import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveComponent as _resolveComponent, createBlock as _createBlock, withCtx as _withCtx, createVNode as _createVNode, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "markdown-content" }
const _hoisted_2 = ["innerHTML"]
const _hoisted_3 = {
  key: 0,
  style: {"display":"none"},
  "aria-hidden": "true"
}

import { computed, nextTick, ref } from "vue";
import DOMPurify from "dompurify";
import katex from "katex";
import { marked } from "marked";
import "katex/dist/katex.min.css";

const PLACEHOLDER_PREFIX = "@@KATEX";

export default /*@__PURE__*/_defineComponent({
  __name: 'MarkdownContent',
  props: {
    content: {},
    preview: { type: Boolean },
    resolveImageUrl: { type: Function }
  },
  setup(__props: any) {

const props = __props;

const previewImages = ref<string[]>([]);
const previewVisible = ref(false);
const previewCurrent = ref(0);
const handlePreviewVisibleChange = (visible: boolean) => {
  previewVisible.value = visible;
};
const handleImageClick = async (event: MouseEvent) => {
  if (
    !props.preview ||
    !(event.target instanceof HTMLImageElement) ||
    !(event.currentTarget instanceof HTMLElement)
  ) return;
  const images = Array.from(event.currentTarget.querySelectorAll("img"));
  previewImages.value = images.map((image) => image.currentSrc || image.src);
  previewCurrent.value = images.indexOf(event.target);
  await nextTick();
  previewVisible.value = true;
};

// 试题文本中的公式以 $$...$$ 包裹（切题接口输出约定）
const MATH_PATTERN = /\$\$([\s\S]+?)\$\$/g;
const PLACEHOLDER_PATTERN = /@@KATEX(\d+)@@/g;

// 只有 ASCII 的 “.” 和 “)” 会被 Markdown 当成有序列表标记，行首出现时会被重排缩进，需转义；
// 中文顿号“、”、全角句点“．”既不是列表标记，CommonMark 也不会反转义，转义它们反而会残留可见反斜杠
const QUESTION_NUMBER_PATTERN = /^(\s*)(\d{1,2})([.)])(?=\s)/gm;
// 清理历史数据里被错误转义的反斜杠（如 “1\．”“1\、”，全角标点前 marked 不会反转义）
const BROKEN_ESCAPE_PATTERN = /\\+([^\x00-\x7F])/g;

// 先把公式抽成占位符（避免 marked 破坏 LaTeX 语法），解析 Markdown 后再换回渲染结果
const renderRichText = (source?: string | null): string => {
  const text = String(source ?? "");
  if (!text.trim()) return "";

  const mathBlocks: string[] = [];
  const withPlaceholders = text
    .replace(MATH_PATTERN, (match, tex) => {
      try {
        mathBlocks.push(
          katex.renderToString(tex, { displayMode: false, throwOnError: false, strict: false })
        );
        return `${PLACEHOLDER_PREFIX}${mathBlocks.length - 1}@@`;
      } catch {
        return match;
      }
    })
    .replace(QUESTION_NUMBER_PATTERN, (_match, indent, number, dot) => `${indent}${number}\\${dot}`)
    .replace(BROKEN_ESCAPE_PATTERN, "$1");

  const markdownHtml = marked.parse(withPlaceholders, { breaks: true, gfm: true }) as string;
  const merged = markdownHtml.replace(PLACEHOLDER_PATTERN, (_match, index) =>
    mathBlocks[Number(index)] ?? ""
  );
  // KaTeX 会输出 MathML 与 SVG 片段，需放开对应白名单
  const sanitized = DOMPurify.sanitize(merged, {
    USE_PROFILES: { html: true, mathMl: true, svg: true },
  });
  const container = document.createElement("div");
  container.innerHTML = sanitized;
  container.querySelectorAll("img").forEach((image) => {
    // 鉴权图片加载后是 blob 地址，在 HTML 清理后替换，避免被过滤。
    const src = image.getAttribute("src");
    if (src && props.resolveImageUrl) image.src = props.resolveImageUrl(src);
    image.setAttribute("loading", "lazy");
    image.setAttribute("decoding", "async");
  });
  return container.innerHTML;
};

const html = computed(() => renderRichText(props.content));

return (_ctx: any,_cache: any) => {
  const _component_a_image = _resolveComponent("a-image")!
  const _component_a_image_preview_group = _resolveComponent("a-image-preview-group")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      onClick: handleImageClick,
      innerHTML: html.value
    }, null, 8, _hoisted_2),
    (__props.preview && previewImages.value.length)
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          _createVNode(_component_a_image_preview_group, {
            preview: { visible: previewVisible.value, current: previewCurrent.value, onVisibleChange: handlePreviewVisibleChange }
          }, {
            default: _withCtx(() => [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(previewImages.value, (src, index) => {
                return (_openBlock(), _createBlock(_component_a_image, {
                  key: `${index}-${src}`,
                  src: src
                }, null, 8, ["src"]))
              }), 128))
            ]),
            _: 1
          }, 8, ["preview"])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})